<template>
  <div>
    <!-- Search Field with Filter Button -->
    <div class="px-2 mt-3 mb-0">
      <v-text-field
        v-model="search"
        placeholder="Zoeken in veldwerkverslagen..."
        solo
        clearable
        @click:clear="clearSearch"
        @input="onSearchInput"
      >
        <template #append>
          <CounterBadgeIcon
            :counter="filter_window.selected_options"
            icon="mdi-filter"
            @click="openFilterWindow"
          />
          <v-btn
            icon
            variant="text"
            title="Ververs lijst vanaf API"
            @click="refreshData"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </template>
      </v-text-field>
    </div>

    <!-- Loading State -->
    <v-fade-transition>
      <v-sheet
        v-if="dayReportSearchStore.loading && !day_reports.length"
        class="d-flex flex-column align-center py-8 rounded"
        color="blue-lighten-5"
      >
        <v-progress-circular
          indeterminate
          color="primary"
          class="mb-3"
        ></v-progress-circular>
        <div class="text-body-1 font-weight-medium text-primary">
          Veldwerkverslagen worden geladen...
        </div>
      </v-sheet>
    </v-fade-transition>

    <!-- Empty State -->
    <v-fade-transition>
      <v-sheet
        v-if="!dayReportSearchStore.loading && !day_reports.length"
        class="d-flex flex-column align-center py-8 rounded"
        color="warning-lighten-5"
      >
        <v-icon
          icon="mdi-alert"
          color="warning"
          size="large"
          class="mb-3"
        ></v-icon>
        <div class="text-body-1 font-weight-medium text-warning-darken-2">
          Geen veldwerkverslagen gevonden met deze filters
        </div>
        <v-btn
          variant="text"
          color="primary"
          class="mt-3"
          prepend-icon="mdi-filter-remove"
          @click="clearSearch"
        >
          Filters wissen
        </v-btn>
      </v-sheet>
    </v-fade-transition>

    <!-- Day Reports List -->
    <v-card
      v-if="day_reports.length > 0"
      variant="flat"
      class="rounded-lg day-reports-container mt-2"
    >
      <v-list class="pa-2" density="default">
        <template
          v-for="(day_report, index) in day_reports"
          :key="day_report.id"
        >
          <DayReportListItem :day_report="day_report" />
          <v-divider v-if="index < day_reports.length - 1" class="mx-4" />
        </template>
      </v-list>

      <!-- Load More Section -->
      <div class="d-flex justify-center py-4">
        <v-progress-circular
          v-if="dayReportSearchStore.loading"
          indeterminate
          color="primary"
        ></v-progress-circular>

        <v-btn
          v-else-if="canLoadMore"
          ref="loadMoreTrigger"
          color="primary"
          variant="outlined"
          prepend-icon="mdi-refresh"
          rounded
          @click="loadMore"
        >
          Meer laden
        </v-btn>
      </div>

      <!-- Infinite loading trigger (invisible) -->
      <div
        v-if="!dayReportSearchStore.loading && canLoadMore"
        ref="infiniteLoadingTrigger"
        class="infinite-trigger"
        style="height: 20px"
      ></div>
    </v-card>

    <!-- End of data message -->
    <div
      v-if="
        !dayReportSearchStore.loading && !canLoadMore && day_reports.length > 0
      "
      class="d-flex justify-center py-6"
    >
      <v-card
        class="text-center pa-6"
        variant="flat"
        color="grey-lighten-5"
        max-width="400"
        rounded="lg"
        elevation="0"
      >
        <div class="end-of-data-container">
          <div class="icon-wrapper mb-4">
            <v-avatar size="56" color="success-lighten-3" class="mb-2">
              <v-icon size="28" color="success-darken-1">
                mdi-check-circle-outline
              </v-icon>
            </v-avatar>
          </div>

          <div class="content-wrapper">
            <h4 class="text-h6 text-grey-darken-2 mb-2 font-weight-medium">
              Alle veldwerkverslagen geladen
            </h4>
            <p
              class="text-body-2 text-grey-darken-1 mb-0"
              style="line-height: 1.4"
            >
              Er zijn geen verslagen meer om te laden.
              <br />
              <span class="font-weight-medium">
                {{ day_reports.length }} verslag{{
                  day_reports.length !== 1 ? "en" : ""
                }}
              </span>
              gevonden.
            </p>
          </div>
        </div>
      </v-card>
    </div>
  </div>

  <!-- Filter Dialog -->
  <DayReportFilterDialog
    :model-value="filter_window"
    @update:model-value="filter_window = $event"
    @search="searchDayReports"
  />
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import DayReportListItem from "./DayReportListItem.vue";
import DayReportFilterDialog from "./DayReportFilterDialog.vue";
import CounterBadgeIcon from "@/components/ui/CounterBadgeIcon.vue";
import { useMenuStore } from "@/stores/menuStore";
import { useContextMenuStore } from "@/stores/contextMenuStore";
import { useDayReportSearchStore } from "@/stores/dayReportSearchStore";
import { useAlertStore } from "@/stores/alertStore";
import { useIntersectionObserver } from "@vueuse/core";

// Store instances
const menuStore = useMenuStore();
const contextMenuStore = useContextMenuStore();
const dayReportSearchStore = useDayReportSearchStore();
const alertStore = useAlertStore();

// Reactive state
const search = ref("");
const time = ref(null);
const infiniteLoadingTrigger = ref(null);
const filter_window = ref({
  show: false,
  selected_options: 0,
  q: {
    date_eq: "",
    user_id_eq: "",
    day_report_type_id_eq: "",
    submitted_eq: "",
    day_report_lines_content_cont: "",
  },
});

// Computed properties
const day_reports = computed(() => {
  // Create a new sorted array without mutating the original
  return [...dayReportSearchStore.searchResults].sort(
    (a, b) => new Date(b.date) - new Date(a.date),
  );
});

const canLoadMore = computed(() => {
  // Use the search store's proper infinite loading detection based on meta data
  return dayReportSearchStore.hasMorePages && !dayReportSearchStore.loading;
});

// Context menu options
const context_menu = computed(() => [
  {
    name: "Lijst verversen",
    action: refreshData,
    icon: "mdi-refresh",
  },
]);

// Methods
async function fetchData(isLoadMore = false) {
  if (dayReportSearchStore.loading) return;

  console.log("Starting to fetch day reports...");

  try {
    // Create search params object
    const searchParams = {
      q: {
        ...filter_window.value.q,
        // Add search text as content filter
        day_report_lines_content_cont: search.value || "",
      },
    };

    console.log("Sending search params:", JSON.stringify(searchParams));

    let response;
    if (isLoadMore) {
      // Use search for infinite loading with append=true
      response = await dayReportSearchStore.search(searchParams, true);
    } else {
      // Use search for initial load
      response = await dayReportSearchStore.search(searchParams, false);

      // Only show success message on initial load
      alertStore.success("Veldwerkverslagen geladen");
    }

    console.log(
      "Day reports response from search store:",
      response ? response.length : "none",
    );
    console.log(
      "After fetching, day reports count:",
      dayReportSearchStore.searchResults.length,
    );
  } catch (error) {
    console.error("Error fetching day reports:", error);
    alertStore.error("Probleem bij laden veldwerkverslagen");
  }
}

function onSearchInput() {
  // Clear any existing timeout
  if (time.value) {
    window.clearTimeout(time.value);
    time.value = null;
  }

  // Set timeout to avoid triggering search on every keystroke
  time.value = window.setTimeout(() => {
    console.log("Search triggered with:", search.value);
    // Update the filter window's content filter
    filter_window.value.q.day_report_lines_content_cont = search.value;
    refreshData();
  }, 700);
}

function clearSearch() {
  // Clear the search box
  search.value = "";
  // Make sure filter window state is updated
  filter_window.value.q.day_report_lines_content_cont = "";
  // Reset all filter values
  filter_window.value.q.date_eq = "";
  filter_window.value.q.user_id_eq = "";
  filter_window.value.q.day_report_type_id_eq = "";
  filter_window.value.q.submitted_eq = "";
  filter_window.value.selected_options = 0;
  // Refresh data with the updated filter
  refreshData();
}

function refreshData() {
  // Reset the search results before fetching using the search store method
  dayReportSearchStore.clear();
  fetchData(false);
}

function searchDayReports() {
  refreshData();
}

function openFilterWindow() {
  console.log("Opening filter window");
  // Update filter window visibility
  filter_window.value = {
    ...filter_window.value,
    show: true,
  };
  console.log("Filter window state:", filter_window.value);
}

function loadMore() {
  fetchData(true);
}

// Search input is now handled by onSearchInput method with debounce

// Handle infinite scrolling with intersection observer
const loadMoreTrigger = ref(null);

// Set up intersection observer for infinite loading
const { stop: stopIntersectionObserver } = useIntersectionObserver(
  infiniteLoadingTrigger,
  ([{ isIntersecting }]) => {
    if (isIntersecting && canLoadMore.value && !dayReportSearchStore.loading) {
      console.log("DayReportList: Intersection detected, loading more...");
      loadMore();
    }
  },
  {
    threshold: 0.1,
    rootMargin: "100px",
  },
);

// Lifecycle hooks
onMounted(async () => {
  console.log("DayReportList mounted");
  menuStore.setMenuVisible(true);
  contextMenuStore.addOption(context_menu.value);

  // Initialize search store and fetch data
  try {
    // Initialize search store (sets up real-time bridge if configured)
    await dayReportSearchStore.init();

    // Always perform initial search to get fresh data
    console.log("Performing initial data fetch");
    await fetchData();
  } catch (error) {
    console.error("Error during initial data fetch:", error);
  }

  // Intersection observer is automatically set up by useIntersectionObserver composable
});

onUnmounted(() => {
  // Clean up the intersection observer
  stopIntersectionObserver();

  // Clear any pending timeouts
  if (time.value) {
    clearTimeout(time.value);
  }

  // Clear context menu when component unmounts
  contextMenuStore.reset();
});
</script>

<style scoped>
.day-reports-container {
  border-radius: 12px;
  overflow: hidden;
  background-color: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
}

.v-list {
  background-color: transparent !important;
  padding: 8px;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .day-reports-container {
    border-radius: 8px;
  }

  .v-list {
    padding: 4px;
  }
}
</style>
